import { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { Package, Clock, MapPin, Phone, User, Truck, CheckCircle, AlertTriangle, Globe, Lock, ChevronDown, ChevronUp, Image, Upload } from 'lucide-react';

const ES_MONTHS = ['enero','febrero','marzo','abril','mayo','junio','julio','agosto','septiembre','octubre','noviembre','diciembre'];

const formatDateES = (iso) => {
  if (!iso) return '---';
  const d = new Date(iso);
  return `${d.getDate()} de ${ES_MONTHS[d.getMonth()]}, ${d.getFullYear()}`;
};

const STATUS_MAP = {
  es: {
    'Send Immediately':          { label: 'ENVIADO',   color: 'bg-green-100 text-green-700 border-green-200' },
    'Postpone to Next Delivery': { label: 'PENDIENTE', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
    'Delivered':                 { label: 'ENTREGADO', color: 'bg-blue-100 text-blue-700 border-blue-200' },
    default:                     { label: 'PENDIENTE', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  },
  en: {
    'Send Immediately':          { label: 'SHIPPED',   color: 'bg-green-100 text-green-700 border-green-200' },
    'Postpone to Next Delivery': { label: 'PENDING',   color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
    'Delivered':                 { label: 'DELIVERED', color: 'bg-blue-100 text-blue-700 border-blue-200' },
    default:                     { label: 'PENDING',   color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  },
};

const T = {
  es: {
    loading: 'Cargando...',
    notFound: 'Enlace de rastreo no encontrado o expirado.',
    trackingTitle: 'Rastreo de Paquete',
    packageId: 'ID de Paquete',
    type: 'Tipo',
    weight: 'Peso',
    dimensions: 'Dimensiones',
    trackingNumber: 'Número de Rastreo',
    entryDate: 'Fecha de Ingreso',
    status: 'Estado',
    recipient: 'Destinatario',
    address: 'Dirección',
    phone: 'Teléfono',
    photo: 'Foto del Paquete',
    attachPhoto: 'Adjuntar foto del paquete',
    photoAttached: 'Foto adjuntada exitosamente ✓',
    photoError: 'Error al subir archivo. Intente de nuevo.',
    photoTooBig: 'Archivo muy grande. Máximo 5MB.',
    downloadPdf: 'Descargar PDF',
    viewAll: 'Ver todos mis paquetes',
    enterPhone: 'Ingresa tu número de teléfono para ver todos tus paquetes.',
    phonePlaceholder: 'Ej: 7890-1234',
    unlock: 'Desbloquear',
    cancel: 'Cancelar',
    noAccount: 'No se encontró una cuenta con ese número.',
    tooFewDigits: 'Ingresa más dígitos de tu teléfono.',
    tooManyAttempts: 'Demasiados intentos. Intenta de nuevo más tarde.',
    allPackages: 'Todos mis paquetes',
    close: 'Cerrar',
    pending: 'Pendientes',
    shipped: 'Enviados',
    delivered: 'Entregados',
    activeSession: 'Sesión activa',
    poweredBy: 'Powered by SivarSend 🚚',
  },
  en: {
    loading: 'Loading...',
    notFound: 'Tracking link not found or expired.',
    trackingTitle: 'Package Tracking',
    packageId: 'Package ID',
    type: 'Type',
    weight: 'Weight',
    dimensions: 'Dimensions',
    trackingNumber: 'Tracking Number',
    entryDate: 'Entry Date',
    status: 'Status',
    recipient: 'Recipient',
    address: 'Address',
    phone: 'Phone',
    photo: 'Package Photo',
    attachPhoto: 'Attach package photo',
    photoAttached: 'Photo attached successfully ✓',
    photoError: 'Error uploading file. Try again.',
    photoTooBig: 'File too large. Max 5MB.',
    downloadPdf: 'Download PDF',
    viewAll: 'View all my packages',
    enterPhone: 'Enter your phone number to view all your packages.',
    phonePlaceholder: 'e.g. 7890-1234',
    unlock: 'Unlock',
    cancel: 'Cancel',
    noAccount: 'No account found with that number.',
    tooFewDigits: 'Enter more digits of your phone number.',
    tooManyAttempts: 'Too many attempts. Try again later.',
    allPackages: 'All my packages',
    close: 'Close',
    pending: 'Pending',
    shipped: 'Shipped',
    delivered: 'Delivered',
    activeSession: 'Active session',
    poweredBy: 'Powered by SivarSend 🚚',
  },
};

// ── Phone-unlock modal ──────────────────────────────────────────────────────
const PhoneUnlockModal = ({ lang, onClose, onSuccess }) => {
  const t = T[lang];
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (attempts >= 5) { setError(t.tooManyAttempts); return; }
    setError('');
    setLoading(true);
    const clean = phone.replace(/\D/g, '');
    const { data: rows } = await supabase
      .from('customers')
      .select('id, full_name, phone_number')
      .is('deleted_at', null)
      .ilike('phone_digits', '%' + clean)
      .limit(2);
    const customer = (rows && rows.length === 1) ? rows[0] : null;
    setLoading(false);
    if (!rows || rows.length === 0) {
      setAttempts(a => a + 1);
      setError(t.noAccount);
      return;
    }
    if (rows.length > 1) {
      setAttempts(a => a + 1);
      setError(t.tooFewDigits);
      return;
    }
    const { data: allShipments } = await supabase
      .from('shipments')
      .select('*')
      .eq('customer_id', customer.id)
      .order('entry_timestamp', { ascending: false });
    onSuccess(customer, allShipments || []);
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <Lock size={18} className="text-blue-600" />
          <h3 className="font-bold text-blue-900">{t.viewAll}</h3>
        </div>
        <p className="text-sm text-gray-500 mb-4">{t.enterPhone}</p>
        <form onSubmit={handleSubmit} className="space-y-3">
          <input
            type="tel"
            value={phone}
            onChange={e => setPhone(e.target.value)}
            placeholder={t.phonePlaceholder}
            className="w-full p-3 border border-gray-300 rounded-xl outline-none focus:border-blue-500 text-center text-lg font-mono tracking-widest"
            disabled={attempts >= 5}
          />
          {error && (
            <div className="flex items-center gap-2 text-red-500 text-xs">
              <AlertTriangle size={12} /> {error}
            </div>
          )}
          {attempts > 0 && attempts < 5 && (
            <p className="text-[10px] text-gray-400 text-center">{5 - attempts} intentos restantes</p>
          )}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={loading || attempts >= 5}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-bold py-2.5 rounded-xl transition"
            >
              {loading ? '...' : t.unlock}
            </button>
            <button type="button" onClick={onClose} className="px-4 py-2.5 border rounded-xl text-gray-500 text-sm font-bold">
              {t.cancel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── All-packages panel ──────────────────────────────────────────────────────
const AllPackagesPanel = ({ customer, allShipments, lang, onClose }) => {
  const t = T[lang];
  const [openId, setOpenId] = useState(null);

  const groups = {
    pending:   allShipments.filter(s => s.shipping_status === 'Postpone to Next Delivery'),
    shipped:   allShipments.filter(s => s.shipping_status === 'Send Immediately'),
    delivered: allShipments.filter(s => s.shipping_status === 'Delivered'),
  };

  const statusLabels = { pending: t.pending, shipped: t.shipped, delivered: t.delivered };
  const statusColors  = {
    pending:   'text-yellow-700 bg-yellow-50 border-yellow-200',
    shipped:   'text-green-700 bg-green-50 border-green-200',
    delivered: 'text-blue-700 bg-blue-50 border-blue-200',
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
        <div className="flex justify-between items-center p-5 border-b">
          <div>
            <h3 className="font-bold text-blue-900">{t.allPackages}</h3>
            <p className="text-xs text-gray-400">{customer.full_name}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <span className="text-gray-500 text-sm font-bold">{t.close} ×</span>
          </button>
        </div>
        <div className="overflow-y-auto p-4 space-y-4 flex-1">
          {Object.entries(groups).map(([key, items]) => items.length === 0 ? null : (
            <div key={key}>
              <h4 className={`text-xs font-bold uppercase px-3 py-1 rounded-full border inline-block mb-2 ${statusColors[key]}`}>
                {statusLabels[key]} ({items.length})
              </h4>
              <div className="space-y-2">
                {items.map(s => (
                  <div key={s.id} className="border rounded-xl overflow-hidden">
                    <div
                      className="flex justify-between items-center p-3 cursor-pointer hover:bg-gray-50"
                      onClick={() => setOpenId(openId === s.id ? null : s.id)}
                    >
                      <span className="font-mono font-bold text-blue-600 text-sm">{s.unique_package_id}</span>
                      <div className="flex items-center gap-2">
                        <span className={`text-[9px] font-bold px-2 py-0.5 rounded border uppercase ${s.shipment_type === 'ENCOMIENDA' ? 'bg-orange-100 text-orange-700 border-orange-200' : 'bg-blue-100 text-blue-700 border-blue-100'}`}>
                          {s.shipment_type || 'CASILLERO'}
                        </span>
                        {openId === s.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </div>
                    </div>
                    {openId === s.id && (
                      <div className="p-3 border-t bg-gray-50 text-xs space-y-1 text-gray-600">
                        <p><strong>Tracking:</strong> <span className="font-mono">{s.tracking_number || '---'}</span></p>
                        <p><strong>{t.weight}:</strong> {s.weight_lb || '---'} lbs</p>
                        <p><strong>{t.entryDate}:</strong> {formatDateES(s.entry_timestamp)}</p>
                        {s.shipment_type === 'ENCOMIENDA' && s.recipient_name && (
                          <p><strong>{t.recipient}:</strong> {s.recipient_name}</p>
                        )}
                        {s.photo_url && (
                          s.photo_url.toLowerCase().endsWith('.pdf') ? (
                            <a href={s.photo_url} target="_blank" rel="noreferrer"
                               className="flex items-center gap-1 text-blue-600 font-bold hover:underline mt-1">
                              📄 {t.downloadPdf}
                            </a>
                          ) : (
                            <img src={s.photo_url} alt="pkg" className="mt-2 rounded-lg w-full object-cover max-h-40" />
                          )
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Main TrackingPage ───────────────────────────────────────────────────────
const TrackingPage = ({ token }) => {
  const [lang, setLang] = useState('es');
  const t = T[lang];
  const statusMap = STATUS_MAP[lang];

  const [shipment, setShipment] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  // Photo upload state
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoUploadError, setPhotoUploadError] = useState('');
  const [photoSuccess, setPhotoSuccess] = useState(false);

  // Phone session state
  const [verifiedCustomer, setVerifiedCustomer] = useState(null);
  const [showUnlock, setShowUnlock] = useState(false);
  const [allData, setAllData] = useState(null); // { customer, shipments }

  // ── Fetch all packages for a customer ──────────────────────────────────
  const fetchAllPackages = useCallback(async (customerId, customer) => {
    const { data: allShipments } = await supabase
      .from('shipments')
      .select('*')
      .eq('customer_id', customerId)
      .order('entry_timestamp', { ascending: false });
    setAllData({ customer, shipments: allShipments || [] });
  }, []);

  // ── Load shipment by token ─────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('shipments')
        .select(`*, customers(id, full_name, phone_number)`)
        .eq('tracking_token', token)
        .eq('token_active', true)
        .maybeSingle();

      if (error || !data) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setShipment(data);
      setLoading(false);

      // Record first view (only if not already viewed)
      if (!data.token_viewed_at) {
        await supabase
          .from('shipments')
          .update({ token_viewed_at: new Date().toISOString() })
          .eq('id', data.id);
      }
    };
    if (token) load();
  }, [token]);

  // ── Restore verified session from sessionStorage on page load ──────────
  useEffect(() => {
    const saved = sessionStorage.getItem('verified_customer');
    if (!saved) return;
    try {
      const parsed = JSON.parse(saved);
      setVerifiedCustomer(parsed);
    } catch {
      sessionStorage.removeItem('verified_customer');
    }
  }, []);

  // ── Phone unlock success ───────────────────────────────────────────────
  const handleUnlockSuccess = (customer, allShipments) => {
    // Persist verified customer for this browser session
    sessionStorage.setItem('verified_customer', JSON.stringify({
      id: customer.id,
      full_name: customer.full_name,
      phone_number: customer.phone_number,
      verified_at: new Date().toISOString(),
    }));
    setVerifiedCustomer(customer);
    setShowUnlock(false);
    setAllData({ customer, shipments: allShipments });
  };

  // ── Photo upload ───────────────────────────────────────────────────────
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoUploadError('');

    if (file.size > 5 * 1024 * 1024) {
      setPhotoUploadError(t.photoTooBig);
      return;
    }

    setPhotoUploading(true);
    const fileExt = file.name.split('.').pop();
    const fileName = shipment.unique_package_id + '-' + Date.now() + '.' + fileExt;

    const { error: uploadError } = await supabase.storage
      .from('package-photos')
      .upload(fileName, file);

    if (uploadError) {
      console.error('[TrackingPage] Photo upload error:', uploadError);
      setPhotoUploadError(t.photoError);
      setPhotoUploading(false);
      return;
    }

    const { data: urlData } = supabase.storage
      .from('package-photos')
      .getPublicUrl(fileName);

    await supabase
      .from('shipments')
      .update({ photo_url: urlData.publicUrl })
      .eq('tracking_token', token);

    setShipment({ ...shipment, photo_url: urlData.publicUrl });
    setPhotoUploading(false);
    setPhotoSuccess(true);
  };

  // ── Render guards ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center text-white font-bold">
        {t.loading}
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center text-white gap-4">
        <AlertTriangle size={48} className="text-yellow-400" />
        <p className="font-bold text-lg">{t.notFound}</p>
      </div>
    );
  }

  const statusInfo = statusMap[shipment.shipping_status] || statusMap.default;

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      {/* Header */}
      <div className="bg-[#0f172a] text-white p-4 flex justify-between items-center shadow-md">
        <div className="flex items-center gap-2 font-bold">
          <Truck className="text-yellow-400" size={22} />
          <span className="text-sm">SivarSend</span>
        </div>
        <button
          onClick={() => setLang(lang === 'es' ? 'en' : 'es')}
          className="flex items-center gap-1 text-xs font-bold border border-gray-600 px-2 py-1 rounded-lg text-yellow-400 hover:bg-slate-800 transition"
        >
          <Globe size={13} /> {lang.toUpperCase()}
        </button>
      </div>

      {/* Card */}
      <div className="max-w-lg mx-auto mt-8 px-4 pb-12">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          {/* Top stripe */}
          <div className="bg-[#0f172a] p-5 text-white">
            <p className="text-xs text-slate-400 uppercase tracking-widest mb-1">{t.trackingTitle}</p>
            <div className="flex items-center justify-between">
              <span className="font-mono font-bold text-xl">{shipment.unique_package_id}</span>
              <span className={`text-xs font-bold px-3 py-1 rounded-full border ${statusInfo.color}`}>
                {statusInfo.label}
              </span>
            </div>
          </div>

          {/* Details */}
          <div className="p-5 space-y-3 text-sm">
            <Row icon={<Package size={14}/>} label={t.type} value={
              <span className={`px-2 py-0.5 rounded text-[10px] font-black border uppercase ${shipment.shipment_type === 'ENCOMIENDA' ? 'bg-orange-100 text-orange-700 border-orange-200' : 'bg-blue-100 text-blue-700 border-blue-100'}`}>
                {shipment.shipment_type || 'CASILLERO'}
              </span>
            } />
            <Row icon={<Clock size={14}/>} label={t.entryDate} value={formatDateES(shipment.entry_timestamp)} />
            {shipment.tracking_number && (
              <Row icon={<Package size={14}/>} label={t.trackingNumber} value={<span className="font-mono">{shipment.tracking_number}</span>} />
            )}
            {shipment.weight_lb && (
              <Row icon={<Package size={14}/>} label={t.weight} value={`${shipment.weight_lb} lbs`} />
            )}
            {shipment.dimensions && (
              <Row icon={<Package size={14}/>} label={t.dimensions} value={shipment.dimensions} />
            )}

            {/* Recipient (ENCOMIENDA only) */}
            {shipment.shipment_type === 'ENCOMIENDA' && (
              <div className="pt-3 border-t border-gray-100 space-y-2">
                <p className="text-[10px] font-bold text-orange-600 uppercase">{t.recipient}</p>
                {shipment.recipient_name && (
                  <Row icon={<User size={14}/>}   label=""         value={shipment.recipient_name} />
                )}
                {shipment.recipient_phone && (
                  <Row icon={<Phone size={14}/>}  label={t.phone}  value={shipment.recipient_phone} />
                )}
                {shipment.recipient_address && (
                  <Row icon={<MapPin size={14}/>} label={t.address} value={shipment.recipient_address} />
                )}
              </div>
            )}

            {/* ── Photo section ──────────────────────────────────────────── */}
            <div className="pt-3 border-t border-gray-100">
              <p className="text-[10px] font-bold text-gray-400 uppercase mb-2 flex items-center gap-1">
                <Image size={10} /> {t.photo}
              </p>

              {shipment.photo_url ? (
                // Photo already exists — display it
                shipment.photo_url.toLowerCase().endsWith('.pdf') ? (
                  <a
                    href={shipment.photo_url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 text-blue-600 text-sm font-bold hover:underline"
                  >
                    📄 {t.downloadPdf}
                  </a>
                ) : (
                  <img
                    src={shipment.photo_url}
                    alt="package"
                    className="rounded-xl w-full object-cover max-h-56 border"
                  />
                )
              ) : (
                // No photo yet — show upload button
                <div>
                  {photoSuccess ? (
                    <p className="text-green-600 text-xs font-bold flex items-center gap-1">
                      <CheckCircle size={12} /> {t.photoAttached}
                    </p>
                  ) : (
                    <>
                      <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-blue-600 border border-blue-200 px-3 py-2 rounded-lg hover:bg-blue-50 transition w-fit">
                        <Upload size={13} />
                        {photoUploading ? '...' : t.attachPhoto}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,application/pdf"
                          className="hidden"
                          disabled={photoUploading}
                          onChange={handlePhotoUpload}
                        />
                      </label>
                      {photoUploadError && (
                        <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                          <AlertTriangle size={11} /> {photoUploadError}
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── View all packages button ────────────────────────────────── */}
          <div className="border-t px-5 py-4">
            <button
              onClick={() => {
                if (verifiedCustomer) {
                  // Session active — skip phone modal
                  fetchAllPackages(verifiedCustomer.id, verifiedCustomer);
                } else {
                  setShowUnlock(true);
                }
              }}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-3 rounded-xl text-sm transition flex items-center justify-center gap-2"
            >
              <Lock size={15} /> {t.viewAll}
            </button>
            {verifiedCustomer && (
              <p className="text-center text-gray-400 text-[10px] mt-2">
                {t.activeSession} • {verifiedCustomer.full_name}
              </p>
            )}
          </div>
        </div>

        <p className="text-center text-gray-400 text-xs mt-6">{t.poweredBy}</p>
      </div>

      {/* Modals */}
      {showUnlock && (
        <PhoneUnlockModal
          lang={lang}
          onClose={() => setShowUnlock(false)}
          onSuccess={handleUnlockSuccess}
        />
      )}
      {allData && (
        <AllPackagesPanel
          customer={allData.customer}
          allShipments={allData.shipments}
          lang={lang}
          onClose={() => setAllData(null)}
        />
      )}
    </div>
  );
};

// ── Small helper ────────────────────────────────────────────────────────────
const Row = ({ icon, label, value }) => (
  <div className="flex items-start gap-3 text-gray-700">
    <span className="text-gray-400 mt-0.5">{icon}</span>
    <div className="flex-1 flex justify-between gap-2">
      {label && <span className="text-gray-400 text-xs shrink-0">{label}</span>}
      <span className="text-right font-medium text-xs">{value}</span>
    </div>
  </div>
);

export default TrackingPage;
